import { useEffect, useRef, useMemo } from 'react'
import { X, Loader2, Music } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { tracks } from '../../api/client'
import { usePlayerStore } from '../../stores/player'
import { parseLRC, getCurrentLyricIndex } from '../../utils/lrc-parser'

interface LyricsPanelProps {
  isOpen: boolean
  onClose: () => void
  embedded?: boolean
}

export default function LyricsPanel({ isOpen, onClose, embedded = false }: LyricsPanelProps) {
  const { currentTrack, currentTime } = usePlayerStore()
  const lyricsContainerRef = useRef<HTMLDivElement>(null)

  const { data: lyricsData, isLoading } = useQuery({
    queryKey: ['lyrics', currentTrack?.id],
    queryFn: () => tracks.getLyrics(currentTrack!.id),
    enabled: !!currentTrack?.id && (isOpen || embedded),
  })

  const parsedLyrics = useMemo(() => {
    if (lyricsData?.synced_lyrics) {
      return parseLRC(lyricsData.synced_lyrics)
    }
    return null
  }, [lyricsData?.synced_lyrics])

  const currentLyricIndex = useMemo(() => {
    if (parsedLyrics) {
      return getCurrentLyricIndex(parsedLyrics, currentTime)
    }
    return -1
  }, [parsedLyrics, currentTime])

  useEffect(() => {
    if (currentLyricIndex >= 0 && lyricsContainerRef.current && parsedLyrics) {
      const lyricElement = lyricsContainerRef.current.querySelector(
        `[data-lyric-index="${currentLyricIndex}"]`
      )
      if (lyricElement) {
        lyricElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }
  }, [currentLyricIndex, parsedLyrics])

  if (!isOpen && !embedded) return null

  const trackTitle = currentTrack?.title || 'Unknown Track'
  const artistName =
    currentTrack && 'artist_name' in currentTrack
      ? currentTrack.artist_name
      : 'Unknown Artist'

  const lyricsContent = (
    <div
      ref={lyricsContainerRef}
      className={`${embedded ? 'h-full' : 'flex-1'} overflow-y-auto p-4 scroll-smooth`}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loader2 size={32} className="animate-spin text-primary-400" />
        </div>
      ) : lyricsData?.instrumental ? (
        <div className="flex flex-col items-center justify-center h-full text-dark-400">
          <Music size={48} className="mb-4" />
          <p className="text-lg">Instrumental</p>
        </div>
      ) : parsedLyrics && parsedLyrics.length > 0 ? (
        <div className="space-y-4 py-20">
          {parsedLyrics.map((line, index) => (
            <p
              key={index}
              data-lyric-index={index}
              className={`text-center text-lg transition-all duration-300 ${
                index === currentLyricIndex
                  ? 'text-primary-400 font-semibold scale-105'
                  : index < currentLyricIndex
                    ? 'text-dark-500'
                    : 'text-dark-300'
              }`}
            >
              {line.text}
            </p>
          ))}
        </div>
      ) : lyricsData?.plain_lyrics ? (
        <div className="whitespace-pre-wrap text-dark-200 leading-relaxed">
          {lyricsData.plain_lyrics}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-dark-400">
          <Music size={48} className="mb-4" />
          <p className="text-lg">No lyrics available</p>
        </div>
      )}
    </div>
  )

  if (embedded) {
    return lyricsContent
  }

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div
        className="absolute right-0 top-0 bottom-20 w-96 bg-dark-900 border-l border-dark-700 shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold truncate">{trackTitle}</h2>
            <p className="text-sm text-dark-400 truncate">{artistName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-dark-400 hover:text-dark-200 transition-colors ml-2"
          >
            <X size={20} />
          </button>
        </div>
        {lyricsContent}
      </div>
    </div>
  )
}
